import {Component, attr} from "../Component";
import {element} from "../CustomElement";
import {i18n} from "../../i18n/I18n";

import {Page} from "../flipp-page/Page";
import {FlyerListingItem, FlyerListingItemContext} from "../flipp-flyer-listing-item/FlyerListingItem";
import {LoadingSpinner} from "../flipp-loading/LoadingSpinner";
import {FlyerMetadataJSON} from "../../definitions/BackflippDefinitions";

import {inject} from "../../services/di";
import {LocationService} from "../../services/LocationService";
import {FlyerService} from "../../services/FlyerService";
import {AnalyticsService} from "../../services/AnalyticsService";
import {StorageService} from "../../services/StorageService";
import {ConfigService} from "../../services/ConfigService";
import {FlyerPlacement} from "../../services/definitions/IAnalyticsService";
import {UserAgentService} from "../../services/UserAgentService";
import FlyersSortService from "../../services/FlyersSortService";
import MerchantService from "../../services/MerchantService";
import MetadataService from "../../services/MetadataService";
import BreadcrumbsService from "../../services/BreadcrumbsService";
import {SeoDataService} from "../../services/SeoDataService";
import { SeoService } from "../../services/SeoService";
import {isCrawler} from "../../utils/crawler";
import {TombstoneListingItem} from "../flipp-flyer-listing-item/FlyerListingItem";

interface StorePageAttributes {
	withSidebar?: boolean;
	store?: string;
	merchants?: any;
}

@element("flipp-store-page")
export class StorePage extends Component<StorePageAttributes, any> {
	@attr store: string = "";
	@attr withSidebar: boolean = true;
	@attr merchants: string;

	refs: {
		title: HTMLElement,
		content: HTMLElement,
		page: HTMLElement
	};

	state = {
		scrollTop: 0
	};

	render() {
		return <Page ref="page" show="flyers" onScroll={this._handleScroll}>
			<StorePageContent store={this.props.store} />
		</Page>;
	}

	onRender() {
		this.refs.page.scrollTop = this.state.scrollTop;
	}

	onAttach() {
		this.refs.page.scrollTop = this.state.scrollTop;
	}

	private _handleScroll = (e: Event) => {
		this.state.scrollTop = this.refs.page.scrollTop;
	}

}

interface StorePageContentAttributes {
	store?: string;
	category?: string;
	sort?: string;
}

type StorePageContentModel = [
	any[],
	any,
	FlyerMetadataJSON[],
	FlyerMetadataJSON[]
];

@element("flipp-store-page-content")
export class StorePageContent extends Component<StorePageContentAttributes, StorePageContentModel> {
	@inject location: LocationService;
	@inject analytics: AnalyticsService;
	@inject storage: StorageService;
	@inject config: ConfigService;
	@inject userAgent: UserAgentService;
	@inject flyersSortService: FlyersSortService;
	@inject merchantService: MerchantService;
	@inject metadataService: MetadataService;
	@inject breadcrumbsService: BreadcrumbsService;
	@inject seoDataService: SeoDataService;
	@inject seoService: SeoService;
	@inject flyerService: FlyerService;

	@attr category: string = "All Flyers";
	@attr sort: string = "featured";
	@attr store: string = "";

	async prepare() {
		const [merchantAndFlyers, flyersInLocation, otherFlyersInLocation] = await Promise.all([
			this.merchantService.getMerchantAndFlyers(this.location.locale, this.props.store),
			this.merchantService.getMerchantFlyersInLocation(this.props.store),
			this.merchantService.getTopCategoryPremiumFlyers(),
		]);
		const { merchant, cities }: any = merchantAndFlyers;
		return [cities, merchant, flyersInLocation, otherFlyersInLocation] as StorePageContentModel;
	}

	renderLoading() {
		return <LoadingSpinner />;
	}

	render([cities, merchant, flyersInLocation, otherFlyersInLocation]: StorePageContentModel) {
		this.seoService.insertLoadingMarker(70);
		return (
			<div class="content">
				<h1>{this.getH1(merchant)}</h1>
				{this.getBreadcrumbs()}
				{flyersInLocation.length > 0 ?
					this.renderFlyerInLocation(flyersInLocation)
					: this.renderOtherFlyersBlock(merchant, otherFlyersInLocation)
				}
				<div>
					{this.hasStoreFlyers(cities) ?
						this.renderFlyersBlock(cities, merchant) :
						null
					}
				</div>
			</div>
		);
	}

	private renderOtherFlyersBlock = (merchant: any, flyers: FlyerMetadataJSON[]) => {
		const flyerBlock = flyers.length > 0 ? (
			<div>
				<h2>{i18n("other_flyers_near_you")}</h2>
				{flyers.map((flyer: any, idx: number) => (
					<FlyerListingItem
						premium
						flyerId={flyer.id}
						flyer={JSON.stringify(flyer)}
						context={FlyerListingItemContext.Listing}
						category={this.category}
						onClick={this._handleFlyerClick.bind(this, idx, FlyerPlacement.Promoted)}
					/>
				))}
			</div>
		) : null;

		return (
			<div>
				<TombstoneListingItem merchantLogo={`https://images.wishabi.net/${merchant.large_image_path}`} />
				{flyerBlock}
			</div>
		);
	}

	private renderFlyerInLocation = (flyers: FlyerMetadataJSON[]) => (
		<div>
			<h2>{i18n("flyers_near_you")}</h2>
			{flyers.map((flyer: any, idx: number) => (
				<FlyerListingItem
					premium
					flyerId={flyer.id}
					flyer={JSON.stringify(flyer)}
					context={FlyerListingItemContext.Listing}
					category={this.category}
					onClick={this._handleFlyerClick.bind(this, idx, FlyerPlacement.Promoted)}
				/>
			))}
		</div>
	)

	private renderNoFlyersMessage = (flyers: FlyerMetadataJSON[]) => {
		if (flyers.length > 0) {
			return null;
		}
		return (
			<div class="empty-item-container">
				<div>{i18n("no_flyers_for_store", {
					store: this.props.store
				})}</div>
			</div>
		);
	}

	private renderFlyersBlock = (cities: any[], merchant: any) => (
		<div>
			<h2>{this.getH2(merchant)}</h2>
			{cities.map((city: any) => this.renderCitiesAndFlyers(city, merchant))}
		</div>
	)

	private hasStoreFlyers = (cities: any[]) => cities.some(city => city.flyers.length > 0);

	private renderCitiesAndFlyers = (city: any, merchant: any) => {
		if (!city.flyers.length) {
			return <div />;
		}

		return (
			<div>
				<h2>{city.city}</h2>
				<div>{this.renderFlyers(city, merchant)}</div>
			</div>
		);
	}

	private renderFlyers = (city: any, merchant: any) => {
		return city.flyers.map((flyer: any, idx: any) =>
			(<FlyerListingItem
				premium
				flyerId={flyer.id}
				flyer={JSON.stringify(this.prepareFlyer(flyer, city, merchant))}
				context={FlyerListingItemContext.Listing}
				category={this.category}
				onClick={this._handleFlyerClick.bind(this, idx, FlyerPlacement.Promoted)}
			/>)
		);
	}


	private getH1 = (merchant: any) => {
		const locale = this.metadataService.getLocale() === "fr-ca" ? "fr" : "en";
		let name = "";
		if (merchant.translations && merchant.translations[locale]) {
			if (merchant.translations[locale].display_name) {
				name = merchant.translations[locale].display_name;
			} else if (merchant.translations[locale].name) {
				name = merchant.translations[locale].name;
			}
		}
		return i18n("store_flyers_h1", {
			store: name
		});
	}

	private getH2 = ({ name }: any) => {
		return i18n("store_flyers_h2", {
			store: name
		});
	}

	private getBreadcrumbs = () => {
		if (!isCrawler()) {
			return null;
		}
		const locale = this.metadataService.getLocale();
		const breadcrumbs = this.breadcrumbsService.getBaseBreadcrumbs(locale);
		return <ol class="breadcrumbs">
			{breadcrumbs.map((breadcrumb) => (
				<li>
					<a href={breadcrumb.path}>
						{breadcrumb.label}
					</a>
				</li>
			))}
		</ol>;
	}

	private _handleFlyerClick = (idx: number, placement: FlyerPlacement, e: Event) => {
		this.analytics.trackFlyerClick(this.category, this.sort, placement, idx);
	}

	private prepareFlyer = (flyer: any, city: any, merchant: any): any => {
		return {
			...flyer,
			fsas: null,
			merchant_logo: `https://images.wishabi.net/${merchant.large_image_path}`,
			premium_thumbnail_url: `https://f.wishabi.net/${flyer.thumbnails.premium}`,
			city_state: city.city_state,
			name: flyer.flyer_name,
			merchant: merchant.name,
		};
	}
}
